import { FC, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { reminderService } from '../../services/reminderService';
import { barnsService, Barn } from '../../services/barnsService';
import { OfflineNotice, useIsOffline } from '../../offline';
import BarnFormDialog from './BarnFormDialog';
import { useRBAC } from '../../auth/useRBAC';
import { barnsControls } from '../../auth/pageControls';

const Barns: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isOffline = useIsOffline();
  // barns.controller.ts create/update/archive/restore all declare manage:PATIENTS - a barn is a
  // patient grouping on the server and there is no `manage:barns` slug at all (INV-108).
  const rbac = useRBAC();
  const can = barnsControls(rbac);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingBarn, setEditingBarn] = useState<Barn | null>(null);

  // Care due per barn. The reminder Due List already carries each row's barn,
  // so this needs no new endpoint — it replaces the standalone Reminders page's
  // barn grouping, which is how receptionists plan barn visits and truck routes.
  const { data: dueRows = [], isError: dueError } = useQuery(
    ['reminders', 'due-list', 'barn-counts'],
    () => reminderService.getDueList(),
    { enabled: !isOffline },
  );
  const dueCountByBarn = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of dueRows) {
      if (row.declinedAt) continue;
      if (row.status !== 'PENDING' && row.status !== 'OVERDUE') continue;
      const key = row.barn?.id ?? '__none__';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [dueRows]);

  const { data: barns = [], isLoading } = useQuery(
    ['barns', { includeArchived }],
    () => barnsService.list(includeArchived),
    { enabled: !isOffline },
  );

  const archiveMutation = useMutation(
    ({ barn }: { barn: Barn }) =>
      barn.isArchived ? barnsService.restore(barn.id) : barnsService.archive(barn.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['barns']);
        queryClient.invalidateQueries(['horse-locations']);
      },
    },
  );

  // Full address in the list (street, city, state zip) — per Ali 2026-07-08.
  const formatAddress = (barn: Barn) =>
    [barn.street, barn.city, [barn.state, barn.zip].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ') || '—';

  return (
    <Box sx={{ overflowX: 'hidden', p: { xs: 1, sm: 1.5 } }}>
      <Box
        mb={{ xs: 1, sm: 1.5 }}
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' }, mb: { xs: 0.5, sm: 1 } }}
          >
            Barns
          </Typography>
          <Typography
            color="textSecondary"
            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'block' } }}
          >
            Client stabling locations — contacts, horses, and bulk barn operations
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
            }
            label="Show archived"
          />
          {can.add && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingBarn(null);
                setFormOpen(true);
              }}
              disabled={isOffline}
            >
              Add Barn
            </Button>
          )}
        </Box>
      </Box>

      <OfflineNotice>
        Barn management needs a connection. Reconnect to view and edit barns.
      </OfflineNotice>

      {!isOffline && isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!isOffline && !isLoading && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Address</TableCell>
                <TableCell align="right"># Horses</TableCell>
                <TableCell align="right"># Clients</TableCell>
                <TableCell align="right"># Care Due</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {barns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="textSecondary" align="center" py={3}>
                      No barns yet. Barns are created automatically when a patient gets a
                      location, or add one here.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {barns.map((barn) => (
                <TableRow
                  key={barn.id}
                  hover
                  sx={{ cursor: 'pointer', opacity: barn.isArchived ? 0.55 : 1 }}
                  onClick={() => navigate(`/barns/${barn.id}`)}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight={600}>{barn.name}</Typography>
                      {barn.isArchived && <Chip label="Archived" size="small" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {barn.contactName || '—'}
                    {barn.contactPhone && (
                      <Typography variant="body2" color="textSecondary">
                        {barn.contactPhone}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatAddress(barn)}</TableCell>
                  <TableCell align="right">{barn.horseCount ?? 0}</TableCell>
                  <TableCell align="right">{barn.clientCount ?? 0}</TableCell>
                  <TableCell align="right">
                    {dueError ? (
                      // Fail loud (INV-19): an em-dash here would read as
                      // "nothing due at this barn" when we simply don't know.
                      <Tooltip title="Couldn't load care due — reload to check">
                        <Typography component="span" color="error">
                          ?
                        </Typography>
                      </Tooltip>
                    ) : dueCountByBarn.get(barn.id) ? (
                      <Chip
                        size="small"
                        color="warning"
                        label={dueCountByBarn.get(barn.id)}
                        title="Vaccines, Coggins and other care due or overdue for horses at this barn"
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {can.edit && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingBarn(barn);
                            setFormOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {can.archive && (
                      <Tooltip title={barn.isArchived ? 'Restore' : 'Archive'}>
                        <IconButton
                          size="small"
                          onClick={() => archiveMutation.mutate({ barn })}
                          disabled={archiveMutation.isLoading}
                        >
                          {barn.isArchived ? (
                            <UnarchiveIcon fontSize="small" />
                          ) : (
                            <ArchiveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <BarnFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        barn={editingBarn}
        onSaved={(saved) => {
          if (!editingBarn) navigate(`/barns/${saved.id}`);
        }}
      />
    </Box>
  );
};

export default Barns;
